import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems } from "~/features/app/utils";
import { useNavLayout } from "~/features/app/landing-header/context";
import { Link } from "~/features/ui/link";
import { NavAdditionalLink } from "~/features/app/landing-header/nav-additional-links/nav-additional-link";
import { NavAdditionalDropdown } from "~/features/app/landing-header/nav-additional-links/nav-additional-dropdown";
export const fragment = graphql(`
  fragment LandingNavAdditionalLinks on navAdditionalLinks_NeoField {
    __typename
    ...LandingNavAdditionalLink
    ...LandingNavAdditionalDropdown
  }
`);
type Props = {
  navLinks?: FragmentType<typeof fragment>[];
  variant?: keyof typeof variants;
  backButton?: boolean;
};
const variants = {
  transparent: {
    "> :not([hidden]) ~ :not([hidden])": {
      "borderColor": "rgba(255, 255, 255, 0.2)"
    },
    "borderColor": "rgba(255, 255, 255, 0.2)",
    "backgroundColor": "rgba(0, 7, 4, 0.3)",
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))"
  },
  solid: {
    "> :not([hidden]) ~ :not([hidden])": {
      "borderColor": "rgb(0 7 4 / 0.1)"
    },
    "borderColor": "rgb(0 7 4 / 0.1)",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(244 244 249 / var(--tw-bg-opacity))",
    "--tw-text-opacity": "1",
    "color": "rgb(0 7 4 / var(--tw-text-opacity))"
  },
  highlighted: {
    "> :not([hidden]) ~ :not([hidden])": {
      "borderColor": "rgb(0 7 4 / 0.1)"
    },
    "borderColor": "rgb(0 7 4 / 0.1)",
    "backgroundColor": "var(--white-static)",
    "color": "var(--black-static)"
  }
} as const;
export function NavAdditionalLinks(props: Props) {
  const {
    navLinks,
    variant,
    backButton
  } = props;
  const links = useFragment(fragment, navLinks);
  const {
    navTheme,
    isPageHighlighted
  } = useNavLayout();
  const surface = isPageHighlighted ? "highlighted" : variant || navTheme;
  const hasBackButton = backButton !== false;
  const hasLinks = hasItems(links);
  if (!hasBackButton && !hasLinks) {
    return null;
  }
  return <_StyledUl $_css={[{
    "pointerEvents": "auto",
    "display": "flex",
    "height": "var(--header-height)",
    "> :not([hidden]) ~ :not([hidden])": {
      "--tw-divide-x-reverse": "0",
      "borderRightWidth": "calc(1px * var(--tw-divide-x-reverse))",
      "borderLeftWidth": "calc(1px * calc(1 - var(--tw-divide-x-reverse)))"
    },
    "borderLeftWidth": "1px",
    "borderRightWidth": "1px",
    "borderBottomWidth": "1px",
    "borderColor": "inherit",
    "fontSize": "1.6rem",
    "lineHeight": "1.5",
    "fontWeight": "400",
    "--tw-backdrop-blur": "blur(8px)",
    "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
  }, variants[surface], {
    "transform": "translateZ(0)"
  }]}>
      {hasBackButton && <_StyledLi>
          <_StyledLink href="/" children={<_StyledSpan>
                back to{" "}
                <svg width="97" height="17" viewBox="0 0 97 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 8.3C0 3.393 3.688 0.052 8.415 0.052C13.141 0.052 16.829 3.392 16.829 8.3C16.829 13.154 13.141 16.548 8.415 16.548C3.636 16.548 0 13.207 0 8.3ZM12.935 8.3C12.935 5.637 11.169 3.445 8.415 3.445C5.611 3.445 3.845 5.585 3.845 8.3C3.845 11.014 5.611 13.154 8.415 13.154C11.221 13.154 12.935 11.014 12.935 8.3ZM29.554 9.657V0.365H33.346V9.918C33.346 13.676 30.904 16.652 26.282 16.652C21.555 16.652 19.218 13.729 19.218 9.918V0.365H23.01V9.657C23.01 11.745 24.152 13.154 26.282 13.154C28.412 13.154 29.554 11.744 29.554 9.657ZM39.734 4.593C39.734 5.533 40.514 5.951 41.812 6.107L43.422 6.316C47.006 6.786 49.342 7.934 49.342 11.379C49.342 14.407 47.058 16.548 42.85 16.548C37.812 16.548 35.89 13.728 35.423 11.641H39.475C39.786 12.371 40.773 13.415 42.851 13.415C44.564 13.415 45.395 12.737 45.395 11.693C45.395 10.544 44.513 10.127 43.162 9.918L41.552 9.709C38.02 9.239 35.839 7.882 35.839 4.698C35.839 1.722 38.384 0 42.019 0C46.798 0 48.459 2.505 48.927 4.385H45.032C44.72 3.81 43.837 3.079 42.02 3.079C40.565 3.132 39.734 3.706 39.734 4.593ZM64.873 0.365V3.705H59.419V16.339H55.628V3.654H50.174V0.365H64.873ZM67.054 0.365H79.416V3.497H70.898V6.473H76.663V9.709H70.898V12.998H79.987V16.287H67.054V0.365ZM82.116 0.365H90.271C93.803 0.365 95.88 2.088 95.88 5.325C95.88 7.517 94.893 9.03 93.076 9.709L96.815 16.287H92.764L89.232 10.179H85.908V16.287H82.116V0.365ZM85.908 3.601V6.995H89.907C91.258 6.995 91.985 6.42 91.985 5.325C91.985 4.175 91.258 3.654 89.907 3.654H85.908V3.601Z" fill="currentColor" />
                </svg>
              </_StyledSpan>} $_css2={[{
        "position": "relative",
        "display": "inline-flex",
        "height": "100%",
        "width": "100%",
        "alignItems": "center",
        "justifyContent": "center",
        "paddingLeft": "5.2rem",
        "paddingRight": "5.2rem"
      }, {
        "&::before": {
          "content": "var(--tw-content)",
          "position": "absolute",
          "left": "0",
          "right": "0",
          "top": "0",
          "height": "0.4rem",
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
          "opacity": "0",
          "transitionProperty": "opacity",
          "transitionDuration": "250ms",
          "transitionTimingFunction": "ease-in-out",
          "--tw-content": "''"
        }
      }, {
        "@media (hover: hover)": {
          "&:hover::before": {
            "content": "var(--tw-content)",
            "opacity": "1"
          }
        }
      }]} />
        </_StyledLi>}

      {hasLinks && links.map((item, idx) => {
      if (item.__typename === "navAdditionalLinks_link_BlockType") {
        return <_StyledLi2 key={idx}>
                <_StyledNavAdditionalLink {...item} />
              </_StyledLi2>;
      }
      if (item.__typename === "navAdditionalLinks_dropdown_BlockType") {
        return <_StyledLi3 key={idx}>
                <NavAdditionalDropdown variant={surface} {...item} />
              </_StyledLi3>;
      }
      return null;
    })}
    </_StyledUl>;
}
var _StyledUl = /*#__PURE__*/_styled("ul").withConfig({
  componentId: "sc-sf-1wzn7fq-0"
})`${p => p.$_css}`;
var _StyledLi = _styled2("li").withConfig({
  componentId: "sc-sf-1wzn7fq-1"
})({
  "position": "relative",
  "height": "100%"
});
var _StyledLink = _styled3(Link).withConfig({
  componentId: "sc-sf-1wzn7fq-2"
})`${p => p.$_css2}`;
var _StyledSpan = _styled4("span").withConfig({
  componentId: "sc-sf-1wzn7fq-3"
})({
  "display": "flex",
  "alignItems": "flex-start",
  "columnGap": "0.8rem"
});
var _StyledLi2 = _styled5("li").withConfig({
  componentId: "sc-sf-1wzn7fq-4"
})({
  "position": "relative",
  "height": "100%"
});
var _StyledNavAdditionalLink = _styled6(NavAdditionalLink).withConfig({
  componentId: "sc-sf-1wzn7fq-5"
})({
  "paddingLeft": "4rem",
  "paddingRight": "4rem"
});
var _StyledLi3 = /*#__PURE__*/_styled7("li").withConfig({
  displayName: "nav-additional-links___StyledLi3",
  componentId: "sc-14437mw-0"
})({
  "position": "relative",
  "height": "100%"
});